import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { MDText } from 'i18n-react';
import memoize from 'memoize-one';
import { FREQUENCIES, FREQUENCY_ORIGINS } from './helpers/consts';
import { aggregatedOptionsType, aggregatedSettingsType } from './helpers/propTypes';
import MatrixTable, { Column } from '@adsk/bim360-matrix-react-matrix-table';
import SelectBox from '@adsk/bim360-matrix-react-select-box';
import IconButton from '@adsk/bim360-matrix-react-icon-button';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import Tooltip from '@adsk/bim360-matrix-react-tooltip';
import resetIcon from '@adsk/bim360-matrix-react-assets/reset-small.svg';
import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';

import './_EmailFrequencyTable.scss';

export class EmailFrequencyTable extends PureComponent {
  constructor(props) {
    super(props);
    this.renderFrequencyCell = this.renderFrequencyCell.bind(this);

    this.ALL_FREQUENCIES = Object.values(FREQUENCIES).map(freq => ({
      value: freq,
      label: this.props[TSymbolProp].translate(freq),
      subtext: this.props[TSymbolProp].translate(`${freq}_SUBTEXT`)
    }));
  }

  parseSettingsData = memoize(settings => {
    const getTypeObj = type => ({
      name: settings[type].name,
      id: type,
      frequency: settings[type].value,
      origin: settings[type].origin
    });
    return Object.keys(settings).map(getTypeObj);
  });

  renderFrequencyCell({ cellData, rowData, column }) {
    const { onChange } = this.props;
    const category = rowData && rowData.id;
    const options =
      this.props.options && this.props.options[category] && this.props.options[category]
        ? this.ALL_FREQUENCIES.filter(
          freq =>
            this.props.options &&
              this.props.options[category] &&
              this.props.options[category].includes(freq.value)
        )
        : [];
    let freqObj = options.find(({ value }) => value === cellData);
    const adminOnlyOption = !freqObj && rowData.origin === FREQUENCY_ORIGINS.PROJECT;
    if (adminOnlyOption) {
      freqObj = this.ALL_FREQUENCIES.find(({ value }) => value === cellData);
    }
    return (
      <SelectBox
        className="EmailFrequencyTable__frequency-select-box"
        overlayClassName="EmailFrequencyTable__frequency-select-box-overlay"
        onChange={({ value }) => onChange(rowData.id, value)}
        options={options}
        value={rowData.origin === FREQUENCY_ORIGINS.PROJECT ? null : freqObj}
        placeholder={freqObj ? freqObj.label : undefined}
        container={document.body}
        disabled={column.disabled}
      />
    );
  }

  renderResetCell = ({ rowData, column, container }) => {
    if (!this.props.canEdit || rowData.origin === FREQUENCY_ORIGINS.PROJECT) {
      return null;
    }
    return (
      <Tooltip
        tooltipClassName="EmailFrequencyTable__frequency-reset-tooltip"
        place={Tooltip.Places.TOP}
        content={this.props[TSymbolProp].translate('RESET_TO_DEFAULT')}
        container={document.body}
        theme={Tooltip.Themes.DARK}
      >
        <IconButton onClick={() => this.props.onReset(rowData.id)} disabled={column.disabled}>
          <SvgIcon width="12px" height="12px">
            {resetIcon}
          </SvgIcon>
        </IconButton>
      </Tooltip>
    );
  };

  renderCategoryCell = ({ rowData }) => {
    return <p>{this.props[TSymbolProp].translate(rowData?.name)}</p>;
  };

  render() {
    const settingsData = this.parseSettingsData(this.props.settings);
    return (
      <MatrixTable
        headerHeight={0}
        maxHeight={Number.POSITIVE_INFINITY}
        data={settingsData}
        attachTestId
      >
        <Column
          key="name"
          dataKey="name"
          title={this.props[TSymbolProp].translate('TYPE')}
          cellRenderer={this.renderCategoryCell}
          width={160}
        />
        <Column
          key="frequency"
          dataKey="frequency"
          title={this.props[TSymbolProp].translate('FREQUENCY')}
          width={180}
          cellRenderer={this.renderFrequencyCell}
          flexGrow={1}
          disabled={this.props.disabled}
        />
        <Column
          key="reset"
          width={45}
          cellRenderer={this.renderResetCell}
          disabled={this.props.disabled}
        />
      </MatrixTable>
    );
  }
}

EmailFrequencyTable.propTypes = {
  settings: aggregatedSettingsType.isRequired,
  options: aggregatedOptionsType.isRequired,
  onChange: PropTypes.func,
  onReset: PropTypes.func,
  canEdit: PropTypes.bool,
  disabled: PropTypes.bool
};

export default withLocale(
  EmailFrequencyTable,
  ({ locale }) => new MDText(require(`./i18n/${locale}.json`))
);
